import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';

/**
 * Panel de administración de "Terminales" (números de serie de los relojes
 * físicos de fichaje). Permite asociar a cada id_device un alias legible
 * (p.ej. "Entrada principal") y opcionalmente una ubicación. Estos alias se
 * muestran luego en la tabla de Fichajes y en los exports XLSX/CSV.
 */
export function TerminalesPanel() {
  const { apiFetch, user } = useAuth();
  // Allow both admin and managers/users; backend will enforce RLS per-row
  // via `can_manage`. We still hide the action column for users that have no
  // terminals they can manage (a viewer without centers).
  const canEditAnything = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'user';

  const [detected, setDetected]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(null);   // { id_device, alias, location, notes }
  const [confirmDel, setConfirmDel] = useState(null); // id_device pending deletion
  const [search, setSearch]       = useState('');
  const [reloadTick, setReloadTick] = useState(0);    // bump to force a refresh

  // Initial + on-demand reload. Pattern: bump reloadTick to refetch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/terminal-aliases/detected');
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setDetected(data);
        } else if (!cancelled) {
          toast.error('No se pudieron cargar los terminales detectados');
        }
      } catch {
        if (!cancelled) toast.error('Error de conexión');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiFetch, reloadTick]);

  const reload = () => { setLoading(true); setReloadTick(t => t + 1); };

  const handleSave = async () => {
    if (!editing?.alias?.trim()) {
      toast.error('El alias no puede estar vacío');
      return;
    }
    try {
      const res = await apiFetch('/api/terminal-aliases', {
        method: 'POST',
        body: JSON.stringify({
          id_device: editing.id_device,
          alias:     editing.alias.trim(),
          location:  (editing.location || '').trim() || null,
          notes:     (editing.notes || '').trim() || null,
        }),
      });
      if (res.ok) {
        toast.success(`Alias guardado para ${editing.id_device}`);
        setEditing(null);
        reload();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Error al guardar el alias');
      }
    } catch {
      toast.error('Error de conexión');
    }
  };

  const handleDelete = async () => {
    if (!confirmDel) return;
    try {
      const res = await apiFetch(`/api/terminal-aliases/${encodeURIComponent(confirmDel)}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Alias eliminado');
        setConfirmDel(null);
        reload();
      } else {
        toast.error('Error al eliminar');
      }
    } catch {
      toast.error('Error de conexión');
    }
  };

  const filtered = detected.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (d.id_device || '').toLowerCase().includes(q)
        || (d.alias || '').toLowerCase().includes(q)
        || (d.location || '').toLowerCase().includes(q);
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '18px' }}
    >
      <div>
        <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '16px', fontWeight: 600, color: '#f1f5f9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          Terminales de fichaje
          <span style={{ background: '#451a03', color: '#fbbf24', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>NUEVO</span>
        </h3>
        <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>
          Asocia un nombre legible (p.ej. «Entrada principal») a cada número de serie de terminal.
          El alias se mostrará en la tabla de fichajes y en los exports.
        </p>
      </div>

      <Input
        data-testid="terminales-search"
        placeholder="Buscar por número de serie, alias o ubicación…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
      />

      {/* Stats summary */}
      {!loading && (
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', fontSize: '12px', color: '#64748b' }}>
          <span data-testid="terminales-total">
            <strong style={{ color: '#f1f5f9' }}>{detected.length}</strong> terminales detectados (últimos 60 días)
          </span>
          <span>·</span>
          <span data-testid="terminales-with-alias">
            <strong style={{ color: '#22c55e' }}>{detected.filter(d => d.has_alias).length}</strong> con alias
          </span>
          <span>·</span>
          <span data-testid="terminales-without-alias">
            <strong style={{ color: '#f59e0b' }}>{detected.filter(d => !d.has_alias).length}</strong> sin alias
          </span>
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#1e293b', borderBottom: '1px solid #334155' }}>
                <th style={cellHeader}>Nº de serie</th>
                <th style={cellHeader}>Alias</th>
                <th style={cellHeader}>Ubicación</th>
                <th style={{ ...cellHeader, textAlign: 'right' }}>Fichajes (60d)</th>
                <th style={cellHeader}>Visto por última vez</th>
                <th style={{ ...cellHeader, width: '160px' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>Cargando…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                  No hay terminales {search ? 'que coincidan con la búsqueda' : 'detectados en los últimos 60 días'}.
                </td></tr>
              )}
              {!loading && filtered.map((d) => (
                <tr key={d.id_device} data-testid={`terminal-row-${d.id_device}`} style={{ borderTop: '1px solid #1e293b' }}>
                  <td style={{ ...cellBody, fontFamily: 'IBM Plex Mono', color: '#94a3b8' }}>{d.id_device}</td>
                  <td style={{ ...cellBody, color: d.alias ? '#f1f5f9' : '#475569', fontStyle: d.alias ? 'normal' : 'italic' }}>
                    {d.alias || '— sin etiquetar —'}
                  </td>
                  <td style={{ ...cellBody, color: '#94a3b8' }}>{d.location || ''}</td>
                  <td style={{ ...cellBody, textAlign: 'right', fontFamily: 'IBM Plex Mono', color: '#94a3b8' }}>{d.kpis_60d.toLocaleString('es-ES')}</td>
                  <td style={{ ...cellBody, fontFamily: 'IBM Plex Mono', color: '#94a3b8' }}>{d.last_seen}</td>
                  <td style={cellBody}>
                    {(canEditAnything && d.can_manage) ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          data-testid={`terminal-edit-${d.id_device}`}
                          size="sm"
                          onClick={() => setEditing({ id_device: d.id_device, alias: d.alias || '', location: d.location || '', notes: '' })}
                          style={{ background: '#0f172a', color: '#f1f5f9', height: '30px', padding: '0 12px', border: '1px solid #334155' }}
                        >
                          {d.has_alias ? 'Editar' : 'Etiquetar'}
                        </Button>
                        {d.has_alias && (
                          <Button
                            data-testid={`terminal-delete-${d.id_device}`}
                            size="sm"
                            onClick={() => setConfirmDel(d.id_device)}
                            style={{ background: '#7c2d12', color: '#fef2f2', height: '30px', padding: '0 12px' }}
                          >
                            Eliminar
                          </Button>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#475569', fontSize: '12px' }}>
                        {canEditAnything ? 'Fuera de tus centros' : 'Solo lectura'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}>
          <DialogHeader>
            <DialogTitle>Etiquetar terminal</DialogTitle>
          </DialogHeader>
          {editing && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <Label style={{ color: '#94a3b8', fontSize: '12px' }}>Número de serie</Label>
                <div style={{ marginTop: '4px', fontFamily: 'IBM Plex Mono', color: '#94a3b8', fontSize: '14px' }}>{editing.id_device}</div>
              </div>
              <div>
                <Label htmlFor="alias-input" style={{ color: '#94a3b8', fontSize: '12px' }}>Alias *</Label>
                <Input
                  id="alias-input"
                  data-testid="terminal-alias-input"
                  placeholder="Entrada principal, Almacén Norte…"
                  value={editing.alias}
                  onChange={e => setEditing({ ...editing, alias: e.target.value })}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', marginTop: '4px' }}
                />
              </div>
              <div>
                <Label htmlFor="loc-input" style={{ color: '#94a3b8', fontSize: '12px' }}>Ubicación (opcional)</Label>
                <Input
                  id="loc-input"
                  data-testid="terminal-location-input"
                  placeholder="Cádiz – Recepción"
                  value={editing.location}
                  onChange={e => setEditing({ ...editing, location: e.target.value })}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', marginTop: '4px' }}
                />
              </div>
              <div>
                <Label htmlFor="notes-input" style={{ color: '#94a3b8', fontSize: '12px' }}>Notas (opcional)</Label>
                <Input
                  id="notes-input"
                  data-testid="terminal-notes-input"
                  value={editing.notes}
                  onChange={e => setEditing({ ...editing, notes: e.target.value })}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', marginTop: '4px' }}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setEditing(null)} style={{ background: '#1e293b', color: '#94a3b8' }}>Cancelar</Button>
            <Button data-testid="terminal-save-btn" onClick={handleSave} style={{ background: '#3b82f6', color: '#fff' }}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar alias del terminal {confirmDel}?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: '#94a3b8' }}>
              El terminal seguirá apareciendo en los fichajes con su número de serie, pero se quitará el nombre asociado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="terminal-confirm-delete" onClick={handleDelete} style={{ background: '#dc2626', color: '#fff' }}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}

const cellHeader = {
  textAlign: 'left',
  padding: '12px 14px',
  fontSize: '11px',
  fontWeight: 600,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};
const cellBody = {
  padding: '12px 14px',
  color: '#f1f5f9',
};
